import * as React from "react"
import { StickySentinel } from "@digits-shared/components/Responsive/StickySentinel"
import { ColGroup } from "@digits-shared/components/UI/VirtualizedTable/ColGroup"
import { useVTableLoadingState } from "@digits-shared/components/UI/VirtualizedTable/useVTableLoadingState"
import { VTableBody } from "@digits-shared/components/UI/VirtualizedTable/VTableBody"
import { VTableEmptyBody } from "@digits-shared/components/UI/VirtualizedTable/VTableEmptyBody"
import { VTableFooter } from "@digits-shared/components/UI/VirtualizedTable/VTableFooter"
import { VTableHeader } from "@digits-shared/components/UI/VirtualizedTable/VTableHeader"
import { VTableLoading } from "@digits-shared/components/UI/VirtualizedTable/VTableLoading"
import { type VTableProps } from "@digits-shared/components/UI/VirtualizedTable/vTableTypes"
import { TableEl } from "@digits-shared/DesignSystem/Tables/tableStyles"
import stringHelper from "@digits-shared/helpers/stringHelper"
import useForwardedRef from "@digits-shared/hooks/useForwardedRef"
import { getCoreRowModel, useReactTable } from "@tanstack/react-table"

/*
  COMPONENTS
*/
export const VTable = <TData extends object>({
  ref,
  className,
  loading,
  data,
  columns,
  estimateRowSize,
  onRowClick,
  getRowClassName,
  EmptyTable,
  collapsed,
  meta,
  ...tableOptions
}: VTableProps<TData>) => {
  const tableRef = useForwardedRef(ref)
  const headRef = React.useRef<HTMLTableSectionElement>(null)

  const tableMeta = React.useMemo(
    () => ({ ...meta, loading, onRowClick, getRowClassName }),
    [meta, loading, onRowClick, getRowClassName]
  )

  const table = useReactTable({
    ...tableOptions,
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: tableMeta,
    manualPagination: true,
    autoResetAll: false,
  })

  const { loadingInPlace, loadingInPlaceRows, empty } = useVTableLoadingState(table)
  const classNames = stringHelper.classNames(className, collapsed && "table-collapsed")

  return (
    <>
      {!empty && <StickySentinel stickRef={headRef} rootMargin="0px" disabled={collapsed} />}
      <TableEl ref={tableRef} className={classNames}>
        <ColGroup columns={columns} />
        <VTableHeader ref={headRef} table={table} />

        {!collapsed && (
          <>
            {/* Show existing data when not loading in place */}
            {!empty && (
              <VTableBody
                tableRef={tableRef}
                table={table}
                loadingInPlace={loadingInPlace}
                estimateRowSize={estimateRowSize}
              />
            )}
            {loading && <VTableLoading table={table} rowCount={loadingInPlaceRows} />}
            {empty && <VTableEmptyBody table={table} EmptyTable={EmptyTable} />}

            <VTableFooter table={table} />
          </>
        )}
      </TableEl>
    </>
  )
}
